/**
 * Application Layer Auth Response DTOs
 * These are the types that the presentation layer should use
 * 
 * The application layer transforms domain entities/value objects into these DTOs
 * for the presentation layer to consume.
 */

/**
 * Profile state type - exposed to presentation layer
 */
export type ProfileStateType = 'EMAIL_ONLY' | 'UNIVERSITY_REQUIRED' | 'NAME_REQUIRED' | 'ACTIVE';

/**
 * Auth Tokens DTO for presentation layer
 */
export interface AuthTokensResponseDTO {
    accessToken: string;
    refreshToken: string;
    tokenType: string;
}

/**
 * Send OTP Response DTO for presentation layer
 */
export interface SendOtpResponseDTO {
    cooldownSeconds: number;
    email: string;
    expiresInMinutes: number;
    message: string;
}

/**
 * Verify OTP Response DTO for presentation layer
 */
export interface VerifyOtpResponseDTO {
    email: string;
    isNewUser: boolean;
    profileState: ProfileStateType;
    tokens: AuthTokensResponseDTO;
    userId: string;
    whatsappLinked: boolean;
}

/**
 * User Response DTO for presentation layer
 */
export interface UserResponseDTO {
    userId: string;
    email: string;
    firstName: string;
    middleName: string | null;
    lastName: string;
    displayName: string;
    fullName: string;
    profilePictureUrl: string | null;
    phone: string;
    isActive: boolean;
    isVerified: boolean;
    isBanned: boolean;
    createdAt: Date;
    updatedAt: Date;
    universityId: string | null;
    roles: RoleResponseDTO[];
    permissions: string[];
    profileState: ProfileStateType;
    whatsappLinked: boolean;
}

/**
 * Role Response DTO for presentation layer
 */
export interface RoleResponseDTO {
    roleId: string;
    name: string;
    displayName: string;
    description: string;
    isActive: boolean;
    permissions: string[];
    createdAt: Date;
    updatedAt: Date;
}

/**
 * Onboarding Response DTO for presentation layer
 */
export interface OnboardingResponseDTO {
    user: UserResponseDTO;
}

/**
 * Login Response DTO for presentation layer
 */
export interface LoginResponseDTO {
    user: UserResponseDTO;
    tokens: AuthTokensResponseDTO;
}

/**
 * WhatsApp Link Code Response DTO for presentation layer
 */
export interface WhatsAppLinkCodeResponseDTO {
    linkCode: string;
    expiresInSeconds: number;
    message: string;
}

/**
 * Utility functions for presentation layer
 * These are application-layer concerns, not domain concerns
 */

/**
 * Get the onboarding route based on profile state
 * This is presentation routing logic, belongs in application layer
 */
export function getOnboardingRoute(state: ProfileStateType): string {
    switch (state) {
        case 'EMAIL_ONLY':
        case 'UNIVERSITY_REQUIRED':
            return '/onboarding/university';
        case 'NAME_REQUIRED':
            return '/onboarding/name';
        case 'ACTIVE':
        default:
            return '/';
    }
}

/**
 * Check if profile state requires onboarding
 */
export function requiresOnboarding(state: ProfileStateType): boolean {
    return state !== 'ACTIVE';
}

/**
 * Check if profile is complete (ACTIVE)
 */
export function isProfileComplete(state: ProfileStateType): boolean {
    return state === 'ACTIVE';
}
